import i18n from 'i18n';
import { ClientFields, CreateClientProps } from 'modules/clients/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import clsx from 'clsx';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { toggleMFAToAllUserRequest } from 'modules/clients/actions';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core';
import { useParams } from 'react-router';
import Button from 'components/Button';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import styles from './styles';
import { Query } from '.';

const useStyles = makeStyles(styles);

interface MFAButtonProps {
  client: ClientFields | CreateClientProps;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  query: Query;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  toggleMFAToAllUserRequest: ({ id, query, active }) => void;
}

const MFAButton: React.FC<MFAButtonProps> = ({
  client,
  companySelected,
  role,
  query,
  modalOpen,
  modalClose,
  toggleMFAToAllUserRequest,
}) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();

  const handleMFAForClientUsers = (): void => {
    const modalProps: ModalFields = {
      title: i18n.t('CLIENTS_PAGE.ENABLED_MFA_ALL_USERS_TITLE_KEY'),
      content: i18n.t('CLIENTS_PAGE.ENABLED_MFA_ALL_USERS_MSG_KEY', {
        name: client?.name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              toggleMFAToAllUserRequest({
                id,
                query,
                active: true,
              });
              modalClose();
            }}
          >
            {i18n.t('ACTIVATE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    };

    if (client?.user_mfa_is_active === true) {
      modalProps.title = i18n.t(
        'CLIENTS_PAGE.DISABLED_MFA_ALL_USERS_TITLE_KEY'
      );
      modalProps.content = i18n.t(
        'CLIENTS_PAGE.DISABLED_MFA_ALL_USERS_MSG_KEY',
        {
          name: client?.name,
        }
      );
      modalProps.footer = (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              toggleMFAToAllUserRequest({
                id,
                query,
                active: false,
              });
              modalClose();
            }}
          >
            {i18n.t('DISABLE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      );
    }

    modalOpen(modalProps);
  };

  const mfaButtonClassName = React.useMemo(() => {
    if (!client?.user_mfa_is_active) {
      return `${classes.mfaButton} ${clsx({
        [classes.mfaButtonGreen]: inAdminView(role, companySelected),
        [classes.mfaButtonGray]: inSuperAdminView(role, companySelected),
        [classes.mfaButtonBlue]: inOperatorView(role, companySelected),
        [classes.mfaButtonYellow]: companySelected,
      })}`;
    }
    return undefined;
  }, [
    classes.mfaButton,
    classes.mfaButtonBlue,
    classes.mfaButtonGray,
    classes.mfaButtonGreen,
    classes.mfaButtonYellow,
    client,
    companySelected,
    role,
  ]);

  const buttonMFALabel = React.useMemo(() => {
    if (client?.user_mfa_is_active === true)
      return i18n.t('CLIENTS_PAGE.DISABLED_MFA_ALL_USERS_TITLE_KEY');
    return i18n.t('ACTIVE_MFA_TO_ALL_KEY');
  }, [client]);

  return (
    <Button
      variant="outlined"
      id="mfa_for_all_users_button"
      onClick={handleMFAForClientUsers}
      className={mfaButtonClassName}
    >
      {buttonMFALabel}
    </Button>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, partnerSelected, data },
  environments: { environmentList, isLoading: loadingEnvironments },
  clients: { isLoading },
}: ApplicationState) => ({
  isLoading,
  loadingEnvironments,
  environmentList,
  companySelected,
  partnerSelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  toggleMFAToAllUserRequest,
})(MFAButton);
